import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Box as Wrapper } from '@node-space/storybook-components/dist/Box'
import { Box, Button, Icon, Sheet } from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import {
  AddBeneficiaryFormPayload,
  BeneficiaryDetails,
  BeneficiaryTransferDestination,
} from 'types/beneficiaries'
import { AccountTypes } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { useMappedRoles } from '../../hooks/useMappedRoles'
import { AddBeneficiary, ModalStep } from './AddBeneficiary/AddBeneficiary'
import BeneficiaryTable from './components/BeneficiaryTable'
import { DeleteBeneficiary } from './DeleteBeneficiary/DeleteBeneficiary'
import EditBeneficiary, { EditBeneficiaryModalStep } from './EditBeneficiary/EditBeneficiary'

export const ManageBeneficiaries = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const permissions = useMappedRoles()
  const { profile } = useProfileContext() || {}
  const { options: profileOptions } = profile || {}
  const { twoFactorEnabled: isTwoFactorEnabled } = useTwoFAContext() || {}
  const callbackUrl = searchParams.get('callbackUrl') ?? null

  const [errorMessage, setErrorMessage] = useState('')

  const isDeveloperAccount = profileOptions?.registrationAccountType === AccountTypes.DEVELOPER
  const canEdit = permissions?.manageBeneficiaries?.canEdit
  const { data: countries } = useCountriesQuery(true)

  // Add Beneficiary
  const [isAddBeneficiarySheetOpen, setIsAddBeneficiarySheetOpen] = useState(false)
  const [addBeneficiaryStep, setAddBeneficiaryStep] = useState<ModalStep>(ModalStep.TRANSFER_TYPE)
  const [beneficiaryFormState, setBeneficiaryFormState] = useState<AddBeneficiaryFormPayload>(
    {} as AddBeneficiaryFormPayload
  )

  // Edit Beneficiary
  const [editBeneficiaryStep, setEditBeneficiaryStep] = useState<EditBeneficiaryModalStep>(
    EditBeneficiaryModalStep.EDIT_BENEFICIARY
  )
  const [showEditBeneficiarySheet, setShowEditBeneficiarySheet] = useState<boolean>(false)
  const [beneficiaryToUpdate, setBeneficiaryToUpdate] = useState<BeneficiaryDetails>()

  // Delete Beneficiary
  const [beneficiaryToDelete, setBeneficiaryToDelete] = useState<BeneficiaryDetails>(undefined)
  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_BENEFICIARIES, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const addBeneficiarySheetHeader = useMemo(() => {
    if (addBeneficiaryStep === ModalStep.INTERNATIONAL_BANK_DETAILS) {
      return t('manageBeneficiaries.beneficiaryBankDetails')
    }
    if (addBeneficiaryStep === ModalStep.INTERMEDIARY_BANK_DETAILS) {
      return t('manageBeneficiaries.intermediaryBankDetails')
    }

    if (addBeneficiaryStep === ModalStep.CONFIRMATION) {
      return t('confirmBeneficiaryDetails')
    }

    return t('manageBeneficiaries.addBeneficiaryTitle')
  }, [addBeneficiaryStep])

  const editBeneficiarySheetHeader = useMemo(() => {
    return editBeneficiaryStep === EditBeneficiaryModalStep.EDIT_BENEFICIARY
      ? t('manageBeneficiaries.editBeneficiary')
      : t('confirmBeneficiaryDetails')
  }, [editBeneficiaryStep])

  const handleCloseAddBeneficiarySheet = useCallback(() => {
    setIsAddBeneficiarySheetOpen(false)
    setBeneficiaryFormState({} as AddBeneficiaryFormPayload)
    setAddBeneficiaryStep(ModalStep.TRANSFER_TYPE)
    searchParams?.delete('callbackUrl')
    window.history.pushState({}, document.title, callbackUrl)
  }, [setIsAddBeneficiarySheetOpen])

  const handleOnSuccessCloseModal = useCallback(() => {
    handleCloseAddBeneficiarySheet()

    if (callbackUrl) {
      searchParams?.delete('callbackUrl')
      window.history.pushState({}, document.title, callbackUrl)
      window.location.pathname = callbackUrl
    }
  }, [setIsAddBeneficiarySheetOpen, callbackUrl])

  const handleCloseEditBeneficiarySheet = useCallback(() => {
    setShowEditBeneficiarySheet(false)
    setBeneficiaryToUpdate(undefined)
    setEditBeneficiaryStep(EditBeneficiaryModalStep.EDIT_BENEFICIARY)
  }, [setShowEditBeneficiarySheet, setBeneficiaryToUpdate, setEditBeneficiaryStep])

  const onAddBeneficiaryBackHandler = useCallback(() => {
    switch (addBeneficiaryStep) {
      case ModalStep.TRANSFER_TYPE:
        handleCloseAddBeneficiarySheet()
        break
      case ModalStep.ADD_BENEFICIARY:
        setAddBeneficiaryStep(ModalStep.TRANSFER_TYPE)
        break
      case ModalStep.INTERNATIONAL_BANK_DETAILS:
        setAddBeneficiaryStep(ModalStep.ADD_BENEFICIARY)
        break
      case ModalStep.INTERMEDIARY_BANK_DETAILS:
        setAddBeneficiaryStep(ModalStep.INTERNATIONAL_BANK_DETAILS)
        break
      case ModalStep.CONFIRMATION:
        if (beneficiaryFormState?.transferDestination === BeneficiaryTransferDestination.LOCAL) {
          setAddBeneficiaryStep(ModalStep.ADD_BENEFICIARY)
        } else {
          setAddBeneficiaryStep(ModalStep.INTERMEDIARY_BANK_DETAILS)
        }
        break
      default:
        break
    }
  }, [
    addBeneficiaryStep,
    beneficiaryFormState,
    handleCloseAddBeneficiarySheet,
    setAddBeneficiaryStep,
  ])

  const onEditBeneficiaryBackHandler = useCallback(() => {
    switch (editBeneficiaryStep) {
      case EditBeneficiaryModalStep.EDIT_BENEFICIARY:
        setShowEditBeneficiarySheet(false)
        break
      case EditBeneficiaryModalStep.CONFIRMATION:
        setEditBeneficiaryStep(EditBeneficiaryModalStep.EDIT_BENEFICIARY)
        break
      default:
        setShowEditBeneficiarySheet(false)
    }
  }, [editBeneficiaryStep, setShowEditBeneficiarySheet, setEditBeneficiaryStep])

  const handleResetDeleteBeneficiary = useCallback(
    () => setBeneficiaryToDelete(undefined),
    [setBeneficiaryToDelete]
  )

  const handleEditBeneficiaryName = useCallback(
    (beneficiary: BeneficiaryDetails) => {
      setBeneficiaryToUpdate(beneficiary)
      setShowEditBeneficiarySheet(true)
      setErrorMessage(null)
    },
    [setBeneficiaryToUpdate, setShowEditBeneficiarySheet, setErrorMessage]
  )

  return (
    <Wrapper>
      <PageHeader
        description={t('manageBeneficiaries.description')}
        renderRightSection={
          canEdit && (
            <Button
              onClick={() => setIsAddBeneficiarySheetOpen(true)}
              iconElement={<Icon name="Plus" />}
              disabled={isDeveloperAccount || !isTwoFactorEnabled}
            >
              {t('manageBeneficiaries.addBeneficiary')}
            </Button>
          )
        }
      />

      <BeneficiaryTable
        setBeneficiaryToDelete={setBeneficiaryToDelete}
        setIsAddModalOpen={setIsAddBeneficiarySheetOpen}
        handleEditBeneficiaryName={handleEditBeneficiaryName}
      />

      <Sheet
        isOpen={isAddBeneficiarySheetOpen}
        header={addBeneficiarySheetHeader}
        onBack={onAddBeneficiaryBackHandler}
        onClose={handleCloseAddBeneficiarySheet}
        onOpenChange={setIsAddBeneficiarySheetOpen}
      >
        <Box width="full" paddingY={24}>
          <AddBeneficiary
            addBeneficiaryStep={addBeneficiaryStep}
            setAddBeneficiaryStep={setAddBeneficiaryStep}
            handleCloseModal={handleCloseAddBeneficiarySheet}
            handleOnSuccessCloseModal={handleOnSuccessCloseModal}
            beneficiaryFormState={beneficiaryFormState}
            setBeneficiaryFormState={setBeneficiaryFormState}
          />
        </Box>
      </Sheet>

      {!!beneficiaryToDelete && (
        <DeleteBeneficiary
          beneficiaryDetails={beneficiaryToDelete}
          countries={countries}
          onClose={handleResetDeleteBeneficiary}
        />
      )}

      <Sheet
        isOpen={showEditBeneficiarySheet}
        header={editBeneficiarySheetHeader}
        onBack={onEditBeneficiaryBackHandler}
        onClose={handleCloseEditBeneficiarySheet}
        onOpenChange={setShowEditBeneficiarySheet}
      >
        <Box width="full" paddingY={24}>
          <EditBeneficiary
            setShowEditBeneficiarySheet={setShowEditBeneficiarySheet}
            showEditBeneficiarySheet={showEditBeneficiarySheet}
            errorMessage={errorMessage}
            beneficiaryToUpdate={beneficiaryToUpdate}
            setBeneficiaryToUpdate={setBeneficiaryToUpdate}
            editBeneficiaryStep={editBeneficiaryStep}
            setEditBeneficiaryStep={setEditBeneficiaryStep}
          />
        </Box>
      </Sheet>
    </Wrapper>
  )
}
